import axios from 'axios';
import { API_CONFIG } from './config';
import api from './api';

const partnerApi = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {},
  withCredentials: true // SEC-FIX: Enable credentials to send httpOnly cookies
});

// SEC-FIX: Remove localStorage token reading - use httpOnly cookies instead
// Tokens are now automatically sent via cookies by the browser
partnerApi.interceptors.request.use(
  (config) => {
    // XSRF token for CSRF protection (readable by JS)
    const xsrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];
    
    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = xsrfToken;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

partnerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // SEC-FIX: Clear any legacy localStorage tokens and redirect
      localStorage.removeItem('partnerToken');
      localStorage.removeItem('partnerUser');
      window.location.href = '/partner/login';
    }
    return Promise.reject(error);
  }
);

export const partnerAuthAPI = {
  login: (credentials) => partnerApi.post('/partners/login', credentials),
  logout: () => {
    // Clear legacy localStorage
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerUser');
    // Cookies will be cleared by server or expire
    return Promise.resolve();
  }
};

export const partnerDashboardAPI = {
  getStats: () => partnerApi.get('/partners/dashboard'),
  getReferrals: () => partnerApi.get('/partners/referrals/list'),
  getCommissions: () => partnerApi.get('/partners/commissions/list'),
  getWithdrawals: () => partnerApi.get('/partners/withdrawals/list'),
  requestWithdrawal: (data) => partnerApi.post('/partners/withdrawals/request', data),
  updateProfile: (data) => partnerApi.put('/partners/profile', data),
  getLeads: () => partnerApi.get('/partners/crm/leads'),
  createLead: (data) => partnerApi.post('/partners/crm/leads', data),
  updateLead: (id, data) => partnerApi.put(`/partners/crm/leads/${id}`, data),
  convertLead: (id, data) => partnerApi.post(`/partners/crm/leads/${id}/convert`, data),
  getDemos: () => partnerApi.get('/partners/crm/demos'),
  registerTrial: (data) => partnerApi.post('/partners/crm/trials', data),
  getPlans: () => partnerApi.get('/registration/plans'),
  resetLead: (id) => partnerApi.post(`/partners/crm/leads/${id}/reset`),
  getPackages: () => partnerApi.get('/partners/packages'),

  // ─── Ecosystem: Deal Registrations ───────────────────────────────────────
  getDeals: () => partnerApi.get('/partners/ecosystem/deals'),
  createDeal: (data) => partnerApi.post('/partners/ecosystem/deals', data),
  updateDeal: (id, data) => partnerApi.put(`/partners/ecosystem/deals/${id}`, data),

  // ─── Ecosystem: Lead Registrations (formal partner pipeline) ─────────────
  getLeadRegistrations: () => partnerApi.get('/partners/ecosystem/lead-registrations'),
  createLeadRegistration: (data) => partnerApi.post('/partners/ecosystem/lead-registrations', data),
  updateLeadRegistration: (id, data) => partnerApi.put(`/partners/ecosystem/lead-registrations/${id}`, data),

  // ─── Ecosystem: Targets & Points ─────────────────────────────────────────
  getTargets: () => partnerApi.get('/partners/ecosystem/targets'),
  getPoints: () => partnerApi.get('/partners/ecosystem/points'),

  // ─── Ecosystem: Certifications ───────────────────────────────────────────
  getCertifications: () => partnerApi.get('/partners/ecosystem/certifications'),
  getTeamCertifications: () => partnerApi.get('/partners/ecosystem/certifications/team'),

  // ─── Ecosystem: Agreements ───────────────────────────────────────────────
  getAgreements: () => partnerApi.get('/partners/ecosystem/agreements'),
  acceptAgreement: (id) => partnerApi.post(`/partners/ecosystem/agreements/${id}/accept`),

  // ─── Ecosystem: Partner Network (Sub-Partners) ───────────────────────────
  getSubPartners: () => partnerApi.get('/partners/ecosystem/sub-partners'),
  createSubPartner: (data) => partnerApi.post('/partners/ecosystem/sub-partners', data),
  updateSubPartner: (id, data) => partnerApi.put(`/partners/ecosystem/sub-partners/${id}`, data),
  getSubPartnerPerformance: (id) => partnerApi.get(`/partners/ecosystem/sub-partners/${id}/performance`),
  createSubPartnerTarget: (id, data) => partnerApi.post(`/partners/ecosystem/sub-partners/${id}/targets`, data),

  // ─── Helpdesk (separate from CRM Tickets) ────────────────────────────────
  getHelpdeskTickets: () => partnerApi.get('/partners/helpdesk/tickets'),
  createHelpdeskTicket: (data) => partnerApi.post('/partners/helpdesk/tickets', data),
  getTicketMessages: (id) => partnerApi.get(`/partners/helpdesk/tickets/${id}/messages`),
  addTicketMessage: (id, data) => partnerApi.post(`/partners/helpdesk/tickets/${id}/messages`, data),
  escalateTicket: (id, data) => partnerApi.post(`/partners/helpdesk/tickets/${id}/escalate`, data),

  // ─── Onboarding Workflow ───────────────────────────────────────────────
  getOnboardingStatus: () => partnerApi.get('/partners/onboarding/status'),
  getOnboardingAgreement: () => partnerApi.get('/partners/onboarding/agreement'),
  acceptOnboardingAgreement: (data) => partnerApi.post('/partners/onboarding/accept-agreement', data),
  getOnboardingProfileData: () => partnerApi.get('/partners/onboarding/profile-data'),
  completeOnboardingProfile: (data) => partnerApi.post('/partners/onboarding/complete-profile', data),

  // ─── Billing Invoice Generation ──────────────────────────────────────────
  generateBillingInvoice: (data) => partnerApi.post('/partners/ecosystem/billing-invoice', data, { responseType: 'blob' }),
};

export const partnerAdminAPI = {
  // Agreement content management (Super Admin)
  getActiveAgreement: () => api.get('/partners/admin/agreement'),
  getAllAgreements: () => api.get('/partners/admin/agreement/history'),
  createAgreement: (data) => api.post('/partners/admin/agreement', data),
  updateAgreement: (id, data) => api.put(`/partners/admin/agreement/${id}`, data),
  toggleBypass: (partnerId, bypass) => api.put(`/partners/admin/${partnerId}/onboarding-bypass`, { dashboardBypassAllowed: bypass }),
  setOnboardingStatus: (partnerId, status) => api.put(`/partners/admin/${partnerId}/onboarding-status`, { onboardingStatus: status }),
};

export default partnerApi;

